import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Beaker, Leaf, ShieldCheck, Sparkles } from "lucide-react";
import { Reveal } from "@/components/motion/Reveal";
import { BlogByline } from "@/components/BlogByline";
import { ReadWithLlm } from "@/components/ReadWithLlm";
import { buildBlogHead } from "@/lib/blogMeta";
import { RelatedReading } from "@/components/RelatedReading";
import { References } from "@/components/References";
import { ClusterLinks } from "@/components/ClusterLinks";
import { AuthorityFlow } from "@/components/AuthorityFlow";
import { relatedArticles } from "@/lib/clusters";
import heroAsset from "@/assets/blog/sluggish-liver.jpg.asset.json";
import { ClinicalProductPitch } from "@/components/ClinicalProductPitch";

const URL = "https://truwe.in/blog/sluggish-liver-signs-and-fix";
const TITLE = "Signs of a Sluggish Liver and How to Fix It (India, 2026) | TruWe";

export const Route = createFileRoute("/blog/sluggish-liver-signs-and-fix")({
  component: Article,
  head: () =>
    buildBlogHead({
      slug: "sluggish-liver-signs-and-fix",
      title: TITLE,
      description:
        "Morning fatigue, brain fog, stubborn belly weight, dull skin, 10 signs of a sluggish liver in Indian adults, why NAFLD is common here and the evidence-based fix.",
      ogTitle: "Signs of a Sluggish Liver, and How to Fix It",
      ogDescription:
        "Ten early signs a hardworking Indian liver is falling behind, and a research-backed 90-day protocol that actually reverses it.",
      headline: "Signs of a Sluggish Liver and How to Fix It",
      breadcrumbName: "Sluggish Liver: Signs & Fix",
      datePublished: "2026-07-10",
      dateModified: "2026-07-25",
      ogImage: `https://truwe.in${heroAsset.url}`,
      faqs: [
        {
          q: "Is a sluggish liver the same as fatty liver?",
          a: "Not exactly. A sluggish liver is a functional description, it's underperforming. Fatty liver (NAFLD/MAFLD) is a specific diagnosis on ultrasound. A sluggish liver often becomes fatty liver if the drivers aren't addressed.",
        },
        {
          q: "Which blood tests show a sluggish liver?",
          a: "ALT, AST, GGT and ALP as a first pass. Bilirubin (direct + indirect), albumin and a fasting insulin round out the picture. Even 'high-normal' ALT (30-40) in Indian men is a soft signal.",
        },
        {
          q: "How fast can a sluggish liver reverse?",
          a: "With diet, movement and targeted supplementation, ALT/AST typically drop 20-40% in 12 weeks. Full reversal of early NAFLD is realistic in 6-12 months.",
        },
        {
          q: "Do lemon-water or castor-oil packs help?",
          a: "No meaningful evidence. They're harmless placebos. Real levers are diet (less fructose and seed oils), muscle, sleep, and a small evidence-based ingredient stack.",
        },
      ],
    }),
});

const SIGNS = [
  { name: "Persistent morning fatigue despite 7+ hours of sleep" },
  { name: "Brain fog and slow word recall by mid-morning" },
  { name: "Stubborn upper-abdominal weight that won't shift" },
  { name: "Dull, greyish skin tone and occasional adult acne" },
  { name: "Sensitivity to fatty meals, bloating or nausea after ghee-heavy food" },
  { name: "Sensitivity to alcohol, a single drink hits harder than it used to" },
  { name: "Dark under-eye circles that don't improve with sleep" },
  { name: "Slightly raised ALT, AST or GGT on a routine blood test" },
  { name: "Right-upper-quadrant fullness, a low ache under the right ribcage" },
  { name: "Waking at 1-3 AM (traditional Chinese medicine 'liver time' anecdotal, but common)" },
] as const;

function Article() {
  return (
    <article className="bg-background pb-24 pt-28 md:pt-36">
      <div className="mx-auto max-w-3xl px-4 md:px-8">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand">
            Clinical Library · Liver & Gut
          </p>
          <h1 className="mt-3 font-display text-4xl font-extrabold leading-tight tracking-tight text-brand-deep md:text-5xl">
            Signs of a sluggish liver and how to fix it
          </h1>
          <BlogByline date="2026-07-10" readingTime="10 min read" />
          <div className="mt-4">
            <ReadWithLlm title={TITLE} url={URL} kind="article" />
          </div>
          <p className="mt-4 text-base text-muted-foreground md:text-lg">
            One in three Indian adults now has non-alcoholic fatty liver disease (NAFLD).
            Most don't know it, the liver has no pain receptors, so it stays quiet until
            a scan lights up. Before that stage, it whispers. Here are ten whispers to
            listen for, and the 90-day protocol that turns them around.
          </p>
        <figure className="mt-8 overflow-hidden rounded-3xl border border-border">
            <img
              src={heroAsset.url}
              alt="Signs of a sluggish liver and how to fix it, clinical guide"
              width={1200}
              height={630}
              loading="eager"
              className="h-auto w-full"
            />
          </figure>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="mt-12 font-display text-2xl font-bold text-brand-deep md:text-3xl">
            10 signs a hard-working liver is falling behind
          </h2>
          <div className="mt-5 space-y-3">
            {SIGNS.map((row, i) => (
              <div key={row.name} className="rounded-2xl border border-border bg-card p-4 shadow-sm">
                <div className="flex items-center gap-2">
                  <Beaker className="h-4 w-4 text-brand" />
                  <p className="text-sm font-medium text-brand-deep">
                    {i + 1}. {row.name}
                  </p>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-4 text-sm text-muted-foreground">
            Two or three of these together, get an LFT panel and an upper-abdomen
            ultrasound. Cheap, fast, decisive.
          </p>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="mt-12 font-display text-2xl font-bold text-brand-deep md:text-3xl">
            Why the Indian liver is under pressure
          </h2>
          <ul className="mt-4 space-y-3 text-base text-foreground/85">
            <li>
              <strong>Fructose overload</strong> sweetened tea, juices, mithai, packaged
              biscuits. Fructose is metabolised only in the liver and turned to fat.
            </li>
            <li>
              <strong>Refined seed oils</strong> high-omega-6 oils drive hepatic
              inflammation when they replace ghee and cold-pressed oils.
            </li>
            <li>
              <strong>Alcohol + medications</strong> social drinking layered on
              acidity meds, painkillers and antibiotics adds up.
            </li>
            <li>
              <strong>Sedentary work</strong> muscle is the biggest glucose sink after
              the liver. Less muscle = more hepatic fat.
            </li>
            <li>
              <strong>Late, heavy dinners</strong> the liver never gets its overnight
              fat-burning window.
            </li>
          </ul>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="mt-12 font-display text-2xl font-bold text-brand-deep md:text-3xl">
            The 90-day reset (what actually moves numbers)
          </h2>
          <div className="mt-5 space-y-4">
            {[
              {
                name: "Cut liquid fructose to near zero",
                note: "No juices, sweetened lattes, soft drinks or dessert-in-a-bottle iced coffees. Whole fruit is fine, the fibre slows fructose absorption.",
              },
              {
                name: "Lift heavy 2-3× / week",
                note: "Muscle is a glucose sink. Compound lifts (squat, hinge, press, row) out-perform cardio for reversing NAFLD in trials.",
              },
              {
                name: "12-14 hour overnight fast",
                note: "Finish dinner by 8 PM, break the fast at 8-10 AM. Gives the liver time to drain glycogen and burn its own fat.",
              },
              {
                name: "Coffee, 2-3 cups/day",
                note: "One of the few interventions with consistent hepatoprotective data, lower NAFLD, fibrosis and liver-cancer risk in cohort studies.",
              },
              {
                name: "Evidence-based ingredient stack",
                note: "Liposomal silymarin, NAC, liposomal alpha-lipoic acid, liposomal curcumin, a multi-strain probiotic. See the detailed guide below.",
              },
              {
                name: "Retest LFTs at 12 weeks",
                note: "Numbers are your friend. Expect a 20-40% drop in ALT/AST/GGT, that's the reversal in progress.",
              },
            ].map((row) => (
              <div key={row.name} className="rounded-2xl border border-border bg-card p-5 shadow-sm">
                <div className="flex items-center gap-2">
                  <Beaker className="h-4 w-4 text-brand" />
                  <h3 className="font-display text-lg font-semibold text-brand-deep">{row.name}</h3>
                </div>
                <p className="mt-2 text-sm text-foreground/80">{row.note}</p>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <div className="mt-14 rounded-3xl border border-brand/15 bg-gradient-to-br from-brand/5 via-background to-brand-deep/5 p-8 md:p-10">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-brand">
              <Sparkles className="h-4 w-4" /> The TruWe formulation
            </div>
            <h2 className="mt-3 font-display text-2xl font-bold text-brand-deep md:text-3xl">
              Liver Biome Plus, the evidence stack, in one capsule
            </h2>
            <p className="mt-3 text-base text-foreground/85">
              Liposomal <strong>silymarin, curcumin, alpha-lipoic acid</strong>, plus
              <strong> NAC and a multi-strain probiotic</strong> every ingredient in
              the 90-day protocol, at research-backed doses, in their best-absorbed
              forms. No senna, no fillers, no detox-tea theatre.
            </p>
            <div className="mt-5 grid gap-3 sm:grid-cols-3">
              {[
                { icon: Leaf, label: "Liposomal delivery" },
                { icon: ShieldCheck, label: "Third-party tested" },
                { icon: Beaker, label: "USFDA / GMP / FSSAI facility" },
              ].map(({ icon: Icon, label }) => (
                <div
                  key={label}
                  className="flex items-center gap-2 rounded-xl bg-background/70 px-3 py-3 text-sm text-foreground/85 ring-1 ring-border"
                >
                  <Icon className="h-4 w-4 text-brand" />
                  {label}
                </div>
              ))}
            </div>
            <Link
              to="/products/$handle"
              params={{ handle: "liver-biome-plus" }}
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand px-6 py-3 text-sm font-semibold text-white shadow-luxury transition hover:bg-brand-deep"
            >
              Explore Liver Biome Plus <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <ClinicalProductPitch cluster="liver-gut" />
          <ClusterLinks cluster="liver-gut" />
          <AuthorityFlow cluster="liver-gut" />
          <References slug="sluggish-liver-signs-and-fix" />
          <RelatedReading articles={relatedArticles("sluggish-liver-signs-and-fix")} />
        </Reveal>

        <Reveal delay={0.05}>
          <p className="mt-12 text-xs text-muted-foreground">
            Educational only. Any suspected liver condition, jaundice, dark urine,
            persistent right-side pain, needs a doctor immediately.
          </p>
        </Reveal>
      </div>
    </article>
  );
}
